import type { ReactElement, ReactNode } from "react";
import { Suchfeld } from "./meHuellen.js";
import "./Filterkopf.css";

/**
 * Der FILTERKOPF der vier Objektbereiche (Paket „Filterkopf statt zweiter Spalte",
 * Entscheid Dirk 07.09.2026): Suchfeld, Chip-Leiste, bereichseigene Zusatzfilter — oben
 * über der Objektliste, in voller Breite.
 *
 * **Warum EIN Baustein und nicht vier Köpfe.** Bis zu diesem Paket trug jeder Bereich
 * seinen eigenen Spaltenkopf: dasselbe Suchfeld viermal, dieselbe Auswahlzeile viermal,
 * und im Rechnungs-Bereich zusätzlich eine Chip-Leiste, die es sonst nirgends gab. Vier
 * Fassungen sind vier Gelegenheiten, dass dieselbe Sache an einem Bildschirm anders
 * aussieht oder anders heisst. Die Chip-Leiste des Rechnungs-Bereichs ist das Vorbild und
 * steht seither nur noch HIER.
 *
 * **Zwei Modi, weil die Bereiche zwei Filterlogiken haben — und die bleibt unangetastet**
 * (Spec-Nachtrag: „Statusfilter-Logik … bleibt unverändert"):
 *
 *  - `mehrfach` — der RECHNUNGS-Bereich. Sein Server-Weg nimmt den Status als
 *    wiederholbaren Parameter (`?status=ISSUED&status=PAID`, siehe `searchInvoices`), also
 *    darf die Leiste mehrere Gruppen zugleich einschalten. Vorgabe: alle an. Keine an ist
 *    ein zulässiger Zustand — die Liste sagt dann, dass nichts gewählt ist.
 *  - `einfach` — ANGEBOTE und AUFTRÄGE. Ihr Server-Weg nimmt EINEN Status
 *    (`VorgangsSucheParams.status`), also wählt die Leiste einen aus; ein zweiter Klick auf
 *    denselben Chip führt zurück auf „alle". Eine mehrfach wählbare Leiste über einer
 *    einfach filternden Abfrage verspräche etwas, das die Liste nicht einlösen kann.
 *
 * Der ARTIKEL-Bereich bekommt gar keine Leiste: Ein Artikel hat keinen Status. Seine beiden
 * Filter (Unternehmen, Kategorie) stehen als Zusatzfilter da, wo bei den anderen die
 * Auswahllisten stehen.
 *
 * **Der Zustand liegt NICHT hier.** Der Baustein zeichnet und meldet; gehalten wird die
 * Auswahl vom Bereich, und der wählbare Teil davon steht in der ADRESSE (siehe
 * bereichsfilter.ts). Sonst gäbe es einen zweiten Zustandskanal neben ihr.
 *
 * **CSS:** eigener Block `fkop__` — geteilt wird über Ansichten hinweg kein Block, und ein
 * BAUSTEIN bringt seinen eigenen mit (wie `krg__`, `akat__`).
 */

/**
 * Ein Chip: sein Wert (der Filtername der Adresse) und seine deutsche Beschriftung.
 *
 * Über den Filternamen GETYPT und nicht über `string`: Die drei Tabellen in
 * bereichsfilter.ts sind geschlossene Mengen, und der Baustein reicht den Wert unverändert
 * an den Bereich zurück. Mit `string` müsste jeder Verwender ihn beim Empfang wieder
 * behaupten (`as AngebotsFilter`) — eine Zusicherung, die der Typprüfer nicht mehr deckt
 * und die einen Vertipper in der Tabelle stumm durchliesse.
 */
export type FilterChip<I extends string> = { readonly id: I; readonly label: string };

/** Die Chip-Leiste in ihren zwei Ausprägungen — siehe den Kopf dieser Datei. */
export type FilterChips<I extends string> = {
  /** Der Name der Gruppe für alle, die die Seite hören („Rechnungen nach Status"). */
  readonly label: string;
  /** Die Beschriftung des ersten Chips („Alle Rechnungen") — er schaltet auf die Vorgabe
   *  zurück und ist genau dann gedrückt, wenn nichts eingeschränkt ist. */
  readonly alle: string;
  readonly werte: readonly FilterChip<I>[];
} & (
  | {
      readonly modus: "einfach";
      /** `null` heisst „alle". */
      readonly gewaehlt: I | null;
      readonly onGewaehlt: (id: I | null) => void;
    }
  | {
      readonly modus: "mehrfach";
      /** Die eingeschalteten Werte; alle = Vorgabe, keiner = zulässig (siehe oben). */
      readonly aktiv: readonly I[];
      readonly onAktiv: (ids: readonly I[]) => void;
    }
);

/** Der Typparameter ist der Filtername des Bereichs; ohne Chip-Leiste (Artikel) bleibt er
 *  ungenutzt und fällt auf `string` zurück. */
export type FilterkopfProps<I extends string = string> = {
  /** Der Name des Suchfelds für alle, die die Seite hören („Angebote durchsuchen"). */
  suchLabel: string;
  platzhalter: string;
  suche: string;
  onSuche: (text: string) => void;
  chips?: FilterChips<I>;
  /** Die bereichseigenen Zusatzfilter (Auswahllisten), fertig gezeichnet vom Bereich —
   *  ihre Werte sind bereichsabhängig (Unternehmen; beim Artikel auch die Kategorie), und
   *  ein Baustein, der sie selbst baute, kennte alle vier Bereiche. */
  zusatz?: ReactNode;
  /** Der Anlegen-Knopf bzw. ein Bereichs-Verweis, rechts neben dem Suchfeld. Der
   *  Rechnungs-Bereich hat keinen: Eine Rechnung entsteht am Auftrag, nicht auf Zuruf. */
  aktion?: ReactNode;
  /** Gesperrt, solange ein Vorgang offen bearbeitet wird — dieselbe Sperre, die auch die
   *  Objektliste trägt: Ein Filterwechsel unter einer offenen Bearbeitung risse sie zu. */
  gesperrt?: boolean;
  /**
   * WO der Filterkopf steht — und damit, wie viel Platz er hat.
   *
   * `"inhalt"` (die Vorgabe) ist die Lage vom 07.09.2026: volle Breite, eigener Kasten mit
   * Rahmen. `"spalte"` ist die Lage seit dem 23.09.2026 in den vier Objektbereichen: oben in
   * der Listenspalte des Rahmens, rund 280 bis 320 Pixel breit.
   *
   * Ein MODIFIKATOR und kein zweiter Block (Spec-Nachtrag 23.09.2026, ausdrücklich): Es ist
   * derselbe Kopf mit denselben Teilen — nur der Kasten fällt weg (die Spalte hat schon
   * einen Rand) und die Zusatzfilter nehmen je eine ganze Zeile, weil zwei Auswahllisten
   * nebeneinander in 280 Pixeln keine mehr sind. Ein zweiter Block hiesse, dass jede
   * künftige Änderung am Kopf zweimal gemacht werden muss — und einmal vergessen wird.
   */
  lage?: "inhalt" | "spalte" | undefined;
};

export function Filterkopf<I extends string>(p: FilterkopfProps<I>): ReactElement {
  const gesperrt = p.gesperrt ?? false;
  return (
    <div className={`fkop${p.lage === "spalte" ? " fkop--spalte" : ""}`}>
      {/* `role="search"` sitzt an der obersten Zeile, denn DORT steht das Suchfeld — an
          der Chip-Leiste oder den Auswahllisten beschriftete es die falsche Gruppe. */}
      <div className="fkop__zeile" role="search">
        {/* `me-eingabe art="suche"` über die Hülle `Suchfeld` (P12, Gesamtreview W2). Gesperrt
            heisst beim Element readonly + aria-disabled: Der Fokus bleibt, nichts ändert sich. */}
        <Suchfeld className="fkop__suchfeld" name={p.suchLabel} platzhalter={p.platzhalter}
          wert={p.suche} gesperrt={gesperrt} onText={p.onSuche} />
        {p.aktion !== undefined && <div className="fkop__aktion">{p.aktion}</div>}
      </div>

      {p.chips !== undefined && <Chipleiste chips={p.chips} gesperrt={gesperrt} />}

      {p.zusatz !== undefined && <div className="fkop__zusatz">{p.zusatz}</div>}
    </div>
  );
}

function Chipleiste<I extends string>(
  q: { chips: FilterChips<I>; gesperrt: boolean },
): ReactElement {
  const c = q.chips;
  const alleAn = c.modus === "einfach"
    ? c.gewaehlt === null
    : c.aktiv.length >= c.werte.length;

  /**
   * Gedrückt heisst „diese Gruppe ist HERVORGEHOBEN" — nicht „ihre Zeilen sind zu sehen".
   *
   * Der Unterschied zählt genau im Vorgabezustand: Sind ALLE Gruppen an, ist keine
   * hervorgehoben, sondern die Liste schlicht ungefiltert — und gedrückt ist der
   * Alle-Chip, genau wie in der einfach wählbaren Leiste daneben. Wären dort auch alle
   * Status-Chips gedrückt, stünde eine Reihe voller Akzentfarbe da, die nichts aussagt,
   * und der Alle-Chip sagte dasselbe ein zweites Mal.
   */
  function gedrueckt(id: I): boolean {
    if (c.modus === "einfach") return c.gewaehlt === id;
    return !alleAn && c.aktiv.includes(id);
  }

  function schalte(id: I): void {
    if (c.modus === "einfach") {
      // Ein zweiter Klick auf den gewählten Chip führt zurück auf „alle" — sonst gäbe es
      // keinen Weg aus einer Einschränkung heraus ausser über den Alle-Chip daneben.
      c.onGewaehlt(c.gewaehlt === id ? null : id);
      return;
    }
    /*
     * Aus dem UNGEFILTERTEN Zustand heraus schränkt ein Klick auf GENAU diese Gruppe ein —
     * er schaltet sie nicht ab. Das ist die Erwartung an eine Filterleiste („zeig mir die
     * bezahlten"), und es ist der Weg, den die abgelöste Filterliste in einem Klick bot;
     * das reine Umschalten hätte ihn auf drei Klicks verlängert (erst die anderen drei
     * abschalten). Ab der ersten Einschränkung schaltet jeder weitere Klick um — so kommt
     * man zu zweien, und der „Alle"-Chip führt zurück.
     */
    if (alleAn) { c.onAktiv([id]); return; }
    // In der Reihenfolge der Tabelle einsortieren statt anzuhängen: Die Auswahl reist als
    // Abfrageparameter UND als Adress-Segment mit, und eine stabile Reihenfolge macht
    // beides lesbar.
    c.onAktiv(c.aktiv.includes(id)
      ? c.aktiv.filter((w) => w !== id)
      : c.werte.filter((w) => c.aktiv.includes(w.id) || w.id === id).map((w) => w.id));
  }

  /*
   * Übergang P12-3 (docs/ME-DESIGN.md): Die Chips sind native Umschalter (`aria-pressed`) im
   * Bild einer Pille. me-design hat keinen Umschalt-Chip — `me-chip` kennt kein
   * `aria-pressed`, `me-knopf gedrueckt` ist ein Knopf und keine Pille, und `me-filterleiste`
   * bringt ein festes „Zurücksetzen“, das neben dem Alle-Chip ein zweiter gleicher Weg wäre.
   */
  return (
    <div className="fkop__chips" role="group" aria-label={c.label}>
      <button
        type="button"
        className={`fkop__chip${alleAn ? " fkop__chip--an" : ""}`}
        /* Der Alle-Chip trägt seinen Namen unverändert („Alle Rechnungen"): Er NENNT das
           Ziel, statt eine Handlung zu beschreiben — anders als die Status-Chips daneben,
           die eine Gruppe zu- und abschalten. `aria-pressed` trägt den Zustand; eine Farbe
           allein ist keine Auskunft. */
        aria-pressed={alleAn}
        disabled={q.gesperrt}
        onClick={() => {
          if (c.modus === "einfach") c.onGewaehlt(null);
          else c.onAktiv(c.werte.map((w) => w.id));
        }}
      >
        {c.alle}
      </button>
      {c.werte.map((w) => (
        <button
          key={w.id}
          type="button"
          className={`fkop__chip${gedrueckt(w.id) ? " fkop__chip--an" : ""}`}
          /* Der zugängliche Name sagt, was der Chip TUT: „Bezahlt anzeigen" schaltet eine
             Gruppe in dieser Liste zu. */
          aria-label={`${w.label} anzeigen`}
          aria-pressed={gedrueckt(w.id)}
          disabled={q.gesperrt}
          onClick={() => schalte(w.id)}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}
